// src/pages/Homenew.jsx
import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import PostcodeQuotenew from "./PostcodeQuotenew";
import LiveStrip from "../components/LiveStrip";
import FloatingSupportChat from "../components/FloatingSupportChat";
import PremiumContactStrip from "../components/PremiumContactStrip";
import CcgRouteLhrStatusCompact from "../components/CcgRouteLhrStatusCompact";
import CcgPremiumReviews from "../components/CcgPremiumReviews";
import "./Home.css";

const ENABLE_GLOBAL_SUPPORT_CHAT = process.env.REACT_APP_ENABLE_SUPPORT_CHAT === "true";

const ccgHeroStyle = {
  background:
    'linear-gradient(90deg, rgba(15, 23, 42, 0.98) 0%, rgba(15, 23, 42, 0.9) 43%, rgba(15, 23, 42, 0.34) 69%, rgba(15, 23, 42, 0.18) 100%), url("/ccg-hero.png") right center / auto 100% no-repeat, linear-gradient(135deg, #0f172a, #1f2937)',
  padding: "3rem 1.4rem 4rem",
};

const ccgHeroShellStyle = {
  maxWidth: 1055,
  background:
    "linear-gradient(90deg, rgba(8, 15, 29, 0.94) 0%, rgba(8, 15, 29, 0.88) 60%, rgba(8, 15, 29, 0.58) 100%)",
  backdropFilter: "blur(2px)",
};

const Homenew = () => {
  const scrollToForm = () => {
    const el = document.getElementById("taxi-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="home-page ccg-premium-home">
      {/* 🔍 SEO + Schema: emphasize arrivals guide + Heathrow tools */}
      <Helmet>
        <title>
          Heathrow Taxi, Arrivals Guide &amp; Airport Tools | CheapCabGrab
        </title>

        <meta
          name="description"
          content="Book Heathrow airport taxis and fixed-price transfers across Slough, Windsor, Maidenhead and London. Use our Heathrow Arrival Assistant, Flight Checker, Terminal Finder, Train vs Taxi and Parking vs Taxi tools to plan a stress-free journey."
        />

        <link rel="canonical" href="https://www.cheapcabgrab.com/" />

        <meta
          name="keywords"
          content="Heathrow taxi, Heathrow arrivals guide, Heathrow immigration time, Heathrow flight checker, Heathrow terminal finder, Heathrow train vs taxi, Heathrow parking vs taxi, Slough taxi, Windsor taxi"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Heathrow Taxi, Arrivals Guide &amp; Airport Tools | CheapCabGrab"
        />
        <meta
          property="og:description"
          content="Instant fixed-price Heathrow taxis plus smart tools: Arrival Assistant, Flight Checker, Terminal Finder, Train vs Taxi and Parking vs Taxi. Licensed drivers and 24/7 pre-booked service."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/" />

        {/* JSON-LD – TaxiService */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TaxiService",
            name: "CheapCabGrab – Heathrow & Local Taxi Service",
            url: "https://www.cheapcabgrab.com/",
            serviceType: [
              "Heathrow airport taxi",
              "Airport transfer",
              "Local taxi in Slough, Windsor and Maidenhead",
              "Minibus hire",
              "Coach hire"
            ],
            areaServed: [
              "Heathrow Airport",
              "Gatwick Airport",
              "Luton Airport",
              "Stansted Airport",
              "Slough",
              "Windsor",
              "Maidenhead",
              "London"
            ],
            serviceArea: {
              "@type": "Place",
              name: "Heathrow, London airports and surrounding areas"
            },
            provider: {
              "@type": "Organization",
              name: "CheapCabGrab Ltd"
            }
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      {/* 🟦 HERO */}
      <section className="home-hero" style={ccgHeroStyle}>
        <div className="home-hero-shell" style={ccgHeroShellStyle}>
          <div className="home-hero-copy">
            <h1>Airport Taxi & Local Transfers Powered by Live Intelligence</h1>
            <p>Heathrow, Gatwick, Luton, Stansted and local journeys across Slough, Windsor, Maidenhead and London.</p>
            <p>
              Book reliable airport taxis and fixed-price local transfers in just a few clicks.
              Licensed drivers, 24/7 pickups, flight tracking & secure online payments.
            </p>
          </div>

          <div className="ccg-hero-trust-cards" aria-label="CheapCabGrab trust signals">
            <div className="ccg-hero-trust-card">
              <span>✓</span>
              <strong>Licensed & insured</strong>
              <small>Professional airport transfers</small>
            </div>
            <div className="ccg-hero-trust-card">
              <span>G</span>
              <strong>4.8 ★★★★★</strong>
              <small>Google reviews</small>
            </div>
          </div>

          <div className="home-hero-form-row" id="taxi-quote-form">
            <div className="ccg-quote-module-head">
              <span>Get instant quotes</span>
              <strong><i /> Free • Fast • No obligation</strong>
            </div>
            <PostcodeQuotenew />
            <PremiumContactStrip />
          </div>

          <ul className="home-hero-flags">
            <li>✅ Heathrow, Gatwick, Luton & Stansted specialists</li>
            <li>✅ Fixed upfront fares — no surge pricing</li>
            <li>✅ Licensed & insured drivers + meet & greet</li>
            <li>✔ 1000+ transfers ✔ 4.9★ ratings ✔ Fully licensed</li>
          </ul>

          <div className="home-hero-reviews">
            <CcgPremiumReviews />
          </div>
        </div>
      </section>

      <CcgRouteLhrStatusCompact title="Live Heathrow journey checks" className="ccg-home-lhr" />

      {/* 🧭 Popular Travel Tools */}
      <section className="home-section" id="popular-tools">
        <header className="home-section-header">
          <h2>Popular Travel Tools</h2>
          <p>Not sure what’s best? Compare, plan and book with our smart travel tools.</p>
        </header>

        <div className="home-grid-3">
          <article className="home-card">
            <h3>Arrival Assistant</h3>
            <p>From landing at Heathrow to your front door — step-by-step help.</p>
            <div className="home-links-list" style={{ marginTop: 6 }}>
              <Link to="/arrival-assistant-student">Student</Link>
              <Link to="/arrival-assistant-visitor">Visitor</Link>
              <Link to="/arrival-assistant-worker">Worker</Link>
            </div>
          </article>

          <article className="home-card">
            <h3>When Should I Leave for Heathrow?</h3>
            <p>Avoid stress — know exactly when to leave home for your flight.</p>
            <Link className="home-inline-cta" to="/leave-for-heathrow">
              Check now →
            </Link>
          </article>

          <article className="home-card">
            <h3>Taxi vs Train to Heathrow</h3>
            <p>Compare cost, time and convenience — including luggage and groups.</p>
            <Link className="home-inline-cta" to="/train-vs-taxi">
              Compare options →
            </Link>
          </article>

          <article className="home-card">
            <h3>Flight Checker &amp; Pickup Planner</h3>
            <p>Live flight status with smarter pickup timing.</p>
            <Link className="home-inline-cta" to="/flight-checker">
              Check flight →
            </Link>
          </article>

          <article className="home-card">
            <h3>Heathrow Drop-off Charge Calculator</h3>
            <p>Know terminal charges before you go — avoid surprises.</p>
            <Link className="home-inline-cta" to="/heathrow-dropoff-calculator">
              Calculate charges →
            </Link>
          </article>

          <article className="home-card">
            <h3>Legoland Transfers</h3>
            <p>Family-friendly fixed prices from your postcode.</p>
            <Link className="home-inline-cta" to="/legoland-budget-planner">
              View fares →
            </Link>
          </article>
        </div>
      </section>

      <section className="home-section" id="ema-tools">
        <header className="home-section-header">
          <h2>East Midlands Airport Travel Tools</h2>
          <p>Plan, compare and monitor your journey to East Midlands Airport with live travel intelligence.</p>
        </header>

        <div className="home-grid-3">
          <article className="home-card">
            <h3>East Midlands Airport Live Journey Planner</h3>
            <p>Plan your journey to EMA with live disruption monitoring and travel comparisons.</p>
            <Link className="home-inline-cta" to="/east-midlands-airport/live-journey-planner">
              Open planner →
            </Link>
          </article>

          <article className="home-card">
            <h3>Train vs Taxi &amp; Drop-off Costs (EMA)</h3>
            <p>Compare rail routes, taxi options and drop-off charges before travelling.</p>
            <div className="home-links-list" style={{ marginTop: 6 }}>
              <Link to="/train-vs-taxi-east-midlands-airport">Train vs Taxi</Link>
              <Link to="/east-midlands-airport/drop-off-calculator">Drop-off Calculator</Link>
            </div>
          </article>

          <article className="home-card">
            <h3>How to Get to East Midlands Airport</h3>
            <p>Explore the fastest routes, rail stations and road options to reach EMA.</p>
            <Link className="home-inline-cta" to="/how-to-get-to-east-midlands-airport">
              View guide →
            </Link>
          </article>
        </div>
      </section>




      {/* Floating CTA (desktop) */}
      <button className="home-floating-cta" onClick={scrollToForm}>
        🚕 Book Taxi
      </button>

      {!ENABLE_GLOBAL_SUPPORT_CHAT ? (
        <FloatingSupportChat
          brand="ccg"
          defaultFlow="enquiry"
          position="right"
          showAfterMs={10000}
          nudgeText="Need taxi help?"
        />
      ) : null}

      {/* 🟨 WHY CHEAPCABGRAB FOR AIRPORT & LOCAL TAXIS */}
      <section className="home-section" id="why-taxi">
        <header className="home-section-header">
          <h2>Why Book Your Heathrow & Local Taxi With CheapCabGrab?</h2>
          <p>
            Simple, professional airport and local taxi transfers – fixed prices, licensed
            drivers and clear, online booking.
          </p>
        </header>

        <div className="home-grid-3">
          <article className="home-card">
            <h3>Airport Specialists</h3>
            <p>
              Daily transfers to and from Heathrow, Gatwick, Luton and Stansted –
              including hotels, terminals and London addresses.
            </p>
          </article>

          <article className="home-card">
            <h3>Fixed, Upfront Pricing</h3>
            <p>
              No surge pricing or hidden extras. See your fare before you confirm and pay
              securely online.
            </p>
          </article>

          <article className="home-card">
            <h3>Licensed & Insured Drivers</h3>
            <p>
              All taxis are operated by licensed private hire drivers, fully insured for
              passenger transport and experienced with airport pickups.
            </p>
          </article>
        </div>
      </section>

      {/* ✈️ AIRPORT & HEATHROW CONTENT */}
      <section className="home-section" id="airport-taxis">
        <header className="home-section-header">
          <h2>Heathrow Taxi & London Airport Transfers</h2>
          <p>
            Fixed-price airport taxis for arrivals and departures, with live flight
            tracking and options for meet & greet inside the terminal.
          </p>
        </header>

        <div className="home-grid-3">
          <article className="home-card">
            <h3>Heathrow Taxi & Guides</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/heathrow-taxi">🚖 Heathrow Taxi &amp; Transfers</Link>
              </li>
              <li>
                <Link to="/heathrow">ℹ️ Heathrow Airport Guide</Link>
              </li>
              <li>
                <Link to="/heathrow-hotels">🏨 Heathrow Hotels</Link>
              </li>
              <li>
                <Link to="/heathrow-attractions">🎡 Nearby Attractions</Link>
              </li>
              <li>
                <Link to="/heathrow-dropoff-free-2026">🚫 Heathrow Drop-off Free Guide</Link>
              </li>
            </ul>
          </article>

          <article className="home-card">
            <h3>Popular Airport Routes</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/slough-to-heathrow">Slough ↔ Heathrow</Link>
              </li>
              <li>
                <Link to="/heathrow-to-london">Heathrow ↔ Central London</Link>
              </li>
              <li>
                <Link to="/heathrow-to-windsor">Heathrow ↔ Windsor</Link>
              </li>
              <li>
                <Link to="/heathrow-to-slough">Heathrow ↔ Slough</Link>
              </li>
              <li>
                <Link to="/heathrow-express-vs-taxi">Heathrow Express vs Taxi</Link>
              </li>
            </ul>
          </article>

          <article className="home-card">
            <h3>Other London Airports</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/slough-to-gatwick">Slough ↔ Gatwick</Link>
              </li>
              <li>
                <Link to="/slough-to-luton">Slough ↔ Luton</Link>
              </li>
              <li>
                <Link to="/slough-to-stansted">Slough ↔ Stansted</Link>
              </li>
              <li>
                <Link to="/travelnews">🧭 Live Travel News &amp; Traffic</Link>
              </li>
              <li>
                <Link to="/train-vs-taxi">Train vs Taxi Heathrow</Link>
              </li>
              <li>
                <Link to="/cheapest-way-to-heathrow">Cheapest Way to Heathrow</Link>
              </li>
            </ul>
          </article>
        </div>
      </section>

      {/* 🚕 LOCAL TAXI SERVICES */}
      <section className="home-section" id="local-taxis">
        <header className="home-section-header">
          <h2>Local Taxi Services in Slough, Windsor &amp; Maidenhead</h2>
          <p>
            Everyday local journeys for school runs, shopping, evenings out and station
            transfers – as well as airport trips.
          </p>
        </header>

        <div className="home-grid-3">
          <article className="home-card">
            <h3>Local Town Taxis</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/slough-taxi">🚕 Slough Taxi</Link>
              </li>
              <li>
                <Link to="/windsor-taxi">👑 Windsor Taxi</Link>
              </li>
              <li>
                <Link to="/maidenhead-taxi">🌉 Maidenhead Taxi</Link>
              </li>
              <li>
                <Link to="/slough">About Slough Taxi Area</Link>
              </li>
            </ul>
          </article>

          <article className="home-card">
            <h3>London & City Transfers</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/London">🌆 London Taxi &amp; Transfers</Link>
              </li>
              <li>
                <Link to="/services">🧾 All Taxi &amp; Transfer Services</Link>
              </li>
              <li>
                <Link to="/group">Group &amp; Event Transport</Link>
              </li>
              <li>
                <Link to="/events">Events &amp; Stadium Trips</Link>
              </li>
            </ul>
          </article>

          <article className="home-card">
            <h3>Manage Your Journey</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/manage-booking">🔐 Manage Booking</Link>
              </li>
              <li>
                <Link to="/faq">❓ Frequently Asked Questions</Link>
              </li>
              <li>
                <Link to="/contact">📩 Contact CheapCabGrab</Link>
              </li>
              <li>
                <Link to="/train-vs-taxi">🚆 Train vs Taxi (full)</Link>
              </li>
              <li>
                <Link to="/cheapest-way-to-heathrow">💸 Cheapest Way to Heathrow</Link>
              </li>
            </ul>
          </article>
        </div>
      </section>

      {/* 🚌 GROUP TRAVEL / MINIBUS / COACH */}
      <section className="home-section" id="group-travel">
        <header className="home-section-header">
          <h2>Group Travel, Minibus &amp; Coach Hire</h2>
          <p>
            Travelling as a group for a holiday, wedding, corporate event or match day?
            Book space for everyone and their luggage in one go.
          </p>
        </header>

        <div className="home-grid-3">
          <article className="home-card">
            <h3>Minibus Hire</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/minibus-hire">🚌 Minibus Hire (7–14 seats)</Link>
              </li>
              <li>
                <Link to="/heathrow-minibus-hire">Heathrow Minibus Transfers</Link>
              </li>
            </ul>
          </article>

          <article className="home-card">
            <h3>Coach & Larger Groups</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/coach-hire">🚌 Coach Hire (15–25 seats)</Link>
              </li>
              <li>
                <Link to="/heathrow-coach-hire">Heathrow Coach Transfers</Link>
              </li>
            </ul>
          </article>

          <article className="home-card">
            <h3>Corporate & Events</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/group">Business &amp; Event Shuttles</Link>
              </li>
              <li>
                <Link to="/contact">Enquire About Group Travel</Link>
              </li>
            </ul>
          </article>
        </div>
      </section>

      {/* 👨‍✈️ DRIVERS, FLEET & SAFETY */}
      <section className="home-section" id="drivers-fleet">
        <header className="home-section-header">
          <h2>Our Drivers, Vehicles &amp; Safety</h2>
          <p>
            We work with experienced, licensed drivers and a modern fleet of vehicles to
            keep journeys comfortable and reliable.
          </p>
        </header>

        <div className="home-grid-3">
          <article className="home-card">
            <h3>Drivers</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/our-drivers">👨‍✈️ Our Drivers</Link>
              </li>
              <li>
                <Link to="/driver">Driver Sign-Up</Link>
              </li>
              <li>
                <Link to="/driver-success">Driver Application Success</Link>
              </li>
            </ul>
          </article>

          <article className="home-card">
            <h3>Fleet & Vehicles</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/fleet">🚗 Our Vehicles / Fleet</Link>
              </li>
              <li>
                <Link to="/services">Service Types &amp; Vehicle Options</Link>
              </li>
            </ul>
          </article>

          <article className="home-card">
            <h3>Trust & Legal</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/terms">📄 Terms &amp; Conditions</Link>
              </li>
              <li>
                <Link to="/privacy">🔒 Privacy Policy</Link>
              </li>
            </ul>
          </article>
        </div>
      </section>

      {/* 🟨 FAQ SUMMARY (IMPROVED, HEATHROW-FOCUSED) */}
      <section className="home-section" id="faq-mini">
        <header className="home-section-header">
          <h2>Heathrow &amp; Taxi FAQs</h2>
          <p>Quick answers to the questions Heathrow passengers ask most.</p>
        </header>

        <div className="home-faq-grid">
          <article className="home-card">
            <h3>How long will it take to get out of Heathrow?</h3>
            <p>
              Immigration and baggage times vary by terminal, passport type and time of day.
              Our{" "}
              <Link to="/arrival-assistant" className="faq-link">
                Heathrow Arrival Assistant
              </Link>{" "}
              uses recent queue snapshots and your flight details to give a simple estimate
              from <strong>landing to leaving the arrivals hall</strong>.
            </p>
          </article>

          <article className="home-card">
            <h3>Is taxi or train better from Heathrow to London?</h3>
            <p>
              It depends on your budget, luggage and how many people are travelling. Use our{" "}
              <Link to="/train-vs-taxi" className="faq-link">
                Taxi vs Train
              </Link>{" "}
              tool to compare <strong>time, changes and hassle</strong>, or see fixed taxi
              prices on our{" "}
              <Link to="/heathrow-taxi" className="faq-link">
                Heathrow Taxi
              </Link>{" "}
              page.
            </p>
          </article>

          <article className="home-card">
            <h3>Should I park at Heathrow or book a taxi?</h3>
            <p>
              If you plan to drive, our{" "}
              <Link to="/heathrow-parking-vs-taxi" className="faq-link">
                Parking vs Taxi
              </Link>{" "}
              calculator compares typical Heathrow parking charges with a
              <strong> fixed-price taxi</strong> so you can choose the best option.
            </p>
          </article>

          <article className="home-card">
            <h3>What if my flight is delayed?</h3>
            <p>
              We monitor flight status for airport pickups wherever possible and adjust
              driver arrival times when there are delays. You can also check your flight with
              our{" "}
              <Link to="/flight-checker" className="faq-link">
                Heathrow Flight Checker
              </Link>{" "}
              or read more in our full{" "}
              <Link to="/faq" className="faq-link">
                FAQ section
              </Link>
              .
            </p>
          </article>
        </div>
      </section>

      {/* ✈️ TRAVEL TOOLS – HEATHROW ARRIVALS & SUPPORT */}
      <section className="home-section" id="travel-tools">
        <header className="home-section-header">
          <h2>Heathrow Travel Tools</h2>
          <p>
            Plan your arrival, check your flight and choose the best way to leave Heathrow.
          </p>
        </header>

        <div className="home-quick-tools-card">
          <div className="home-quick-tools-text">
            Fast picks (cost + hassle savers):
          </div>
          <div className="home-quick-tools">
            <Link to="/cheapest-way-to-heathrow" className="home-tool-chip">💸 Cheapest Way to Heathrow</Link>
            <Link to="/cheapest-taxi-to-heathrow" className="home-tool-chip">🚕 Cheapest Taxi to Heathrow</Link>
            <Link to="/train-vs-taxi" className="home-tool-chip">🚆 Train vs Taxi</Link>
            <Link to="/heathrow-dropoff-free-2026" className="home-tool-chip">🚫 Free Heathrow Drop-off Guide</Link>
            <Link to="/heathrow-dropoff-calculator" className="home-tool-chip">🚗 Heathrow Drop-off Fee Calculator</Link>
            <Link to="/heathrow-parking-vs-taxi" className="home-tool-chip">🅿️ Parking vs Taxi (Heathrow)</Link>
            <Link to="/arrival-assistant-student" className="home-tool-chip">🎓 Arrival Assistant (Students)</Link>
            <Link to="/arrival-assistant-visitor" className="home-tool-chip">🧳 Arrival Assistant (Visitors)</Link>
            <Link to="/arrival-assistant-worker" className="home-tool-chip">💼 Arrival Assistant (Workers)</Link>
          </div>
        </div>

        <div className="home-grid-3">
          <article className="home-card">
            <h3>🧭 Heathrow Arrival Guide</h3>
            <p style={{ fontSize: 14, marginBottom: 10 }}>
              From the plane door to meeting your driver or family – personalised to your
              flight, passport type and reason for visit.
            </p>
            <ul className="home-bullet-links">
              <li>
                <Link to="/arrival-assistant-student">🎓 Arrival Assistant (Students)</Link>
              </li>
              <li>
                <Link to="/arrival-assistant-visitor">🧳 Arrival Assistant (Visitors)</Link>
              </li>
              <li>
                <Link to="/arrival-assistant-worker">💼 Arrival Assistant (Workers)</Link>
              </li>
              <li>
                <Link to="/arrival-assistant">🧭 Heathrow Arrival Assistant (Original)</Link>
              </li>
            </ul>
          </article>

          <article className="home-card">
            <h3>✈️ Live Flights &amp; Delays</h3>
            <p style={{ fontSize: 14, marginBottom: 10 }}>
              Check today’s flights and UK travel news before booking your taxi.
            </p>
            <ul className="home-bullet-links">
              <li>
                <Link to="/flight-checker">🔎 Heathrow Flight Checker</Link>
              </li>
              <li>
                <Link to="/travelnews">📰 UK &amp; Heathrow Travel News</Link>
              </li>
            </ul>
          </article>

          <article className="home-card">
            <h3>🚆 Terminals, Trains &amp; Parking</h3>
            <ul className="home-bullet-links">
              <li>
                <Link to="/terminalFinder">🛫 Heathrow Terminal Finder</Link>
              </li>
              <li>
                <Link to="/airlineContacts">📞 Airline Contacts</Link>
              </li>
              <li>
                <Link to="/train-vs-taxi">🚆 Train vs Taxi Comparison</Link>
              </li>
              <li>
                <Link to="/heathrow-parking-vs-taxi">🅿️ Parking vs Taxi at Heathrow</Link>
              </li>
              <li>
                <Link to="/heathrow-dropoff-calculator">🚗 Heathrow Drop-off Fee Calculator</Link>
              </li>
              <li>
                <Link to="/heathrow-dropoff-vs-short-stay">🅿️ Drop-off vs Short Stay</Link>
              </li>
            </ul>
          </article>
        </div>
      </section>

      {/* 🔗 USEFUL LINKS (BOTTOM) */}
      <section className="home-section" id="home-links">
        <header className="home-section-header">
          <h2>Popular Pages &amp; Tools</h2>
        </header>

        <ul className="home-links-list">
          <li>
            <Link to="/heathrow-taxi">Heathrow Taxi Service</Link>
          </li>
          <li>
            <Link to="/arrival-assistant-student">Heathrow Arrival Assistant (Students)</Link>
          </li>
          <li>
            <Link to="/arrival-assistant-visitor">Heathrow Arrival Assistant (Visitors)</Link>
          </li>
          <li>
            <Link to="/arrival-assistant-worker">Heathrow Arrival Assistant (Workers)</Link>
          </li>
          <li>
            <Link to="/flight-checker">Heathrow Flight Checker</Link>
          </li>
          <li>
            <Link to="/train-vs-taxi">Heathrow Train vs Taxi Tool</Link>
          </li>
          <li>
            <Link to="/heathrow-parking-vs-taxi">Heathrow Parking vs Taxi</Link>
          </li>
          <li>
            <Link to="/heathrow-dropoff-calculator">Heathrow Drop-off Calculator</Link>
          </li>
          <li>
            <Link to="/heathrow-dropoff-vs-short-stay">Heathrow Drop-off vs Short Stay</Link>
          </li>
          <li>
            <Link to="/heathrow-blue-badge-dropoff">Heathrow Blue Badge Drop-off</Link>
          </li>
          <li>
            <Link to="/terminalFinder">Heathrow Terminal Finder</Link>
          </li>
          <li>
            <Link to="/airlineContacts">Airline Contact Numbers</Link>
          </li>
          <li>
            <Link to="/travelnews">Travel News &amp; Disruption</Link>
          </li>
          <li>
            <Link to="/slough-taxi">Slough Taxi</Link>
          </li>
          <li>
            <Link to="/windsor-taxi">Windsor Taxi</Link>
          </li>
          <li>
            <Link to="/maidenhead-taxi">Maidenhead Taxi</Link>
          </li>
          <li>
            <Link to="/minibus-hire">Minibus Hire</Link>
          </li>
          <li>
            <Link to="/coach-hire">Coach Hire</Link>
          </li>
        </ul>
      </section>


      {/* Small spacer so mobile content isn’t hidden behind CTAs */}
      <div className="home-bottom-spacer" />
    </div>
  );
};

export default Homenew;
